import { FormAnswer } from '../types'

/**
 * Normalización de los datos de contacto de un lead.
 *
 * El sistema permite formularios totalmente personalizados: cada organización
 * puede renombrar las etiquetas y las claves de sus campos. Por eso el CRM no
 * puede depender de que el campo se llame literalmente `fullName`, `phone`,
 * `company` o `email`.
 *
 * Este módulo identifica esos cuatro datos a partir de:
 *  1. la clave del campo (`telefono`, `movil`, `whatsapp`, `tel`…);
 *  2. la etiqueta visible de la pregunta («¿Cuál es tu teléfono?»);
 *  3. la forma del propio valor (un email tiene `@`, un teléfono son dígitos).
 *
 * Así el nombre y el teléfono aparecen siempre en el embudo y en la tabla,
 * venga el lead del formulario oficial de AVA o de cualquier formulario creado
 * por el usuario.
 */

export interface NormalizedContact {
  fullName: string
  phone: string
  company: string
  email: string
}

export const EMPTY_CONTACT: NormalizedContact = {
  fullName: '',
  phone: '',
  company: '',
  email: '',
}

/** Palabras que identifican cada dato, sin acentos y en minúsculas. */
const PATTERNS: Record<keyof NormalizedContact, string[]> = {
  fullName: [
    'fullname',
    'full_name',
    'nombrecompleto',
    'nombre_completo',
    'nombreyapellidos',
    'nombre',
    'name',
    'firstname',
    'first_name',
    'apellidos',
    'lastname',
    'contacto',
    'comonosllamamos',
  ],
  phone: [
    'phone',
    'telephone',
    'telefono',
    'tlf',
    'tel',
    'movil',
    'celular',
    'whatsapp',
    'wasap',
    'numero',
    'mobile',
  ],
  company: [
    'company',
    'empresa',
    'negocio',
    'marca',
    'organizacion',
    'compania',
    'business',
    'servicio',
    'proyecto',
  ],
  email: ['email', 'correo', 'mail', 'e-mail', 'emailaddress'],
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Texto comparable: sin acentos, sin signos y en minúsculas. */
function plain(value: string): string {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(', ')
  if (typeof value === 'object') return ''
  const text = String(value).trim()
  // Nunca se muestran estos valores en la interfaz.
  if (!text || text === 'undefined' || text === 'null') return ''
  return text
}

/** ¿Parece un teléfono? Al menos 6 dígitos y sin letras relevantes. */
function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '')
  if (digits.length < 6 || digits.length > 20) return false
  return /^[+()\d\s.\-/]+$/.test(value)
}

/** Campo al que corresponde una clave o etiqueta, si se reconoce. */
function classify(rawKey: string): keyof NormalizedContact | null {
  const key = plain(rawKey)
  if (!key) return null
  // El email se comprueba primero: «correo de la empresa» es un email.
  const order: (keyof NormalizedContact)[] = [
    'email',
    'phone',
    'company',
    'fullName',
  ]
  for (const field of order) {
    if (PATTERNS[field].some((pattern) => key.includes(plain(pattern)))) {
      return field
    }
  }
  return null
}

function assign(
  target: NormalizedContact,
  field: keyof NormalizedContact,
  value: string,
): void {
  if (!value || target[field]) return
  target[field] = value
}

/**
 * Deriva nombre, teléfono, empresa y email de un objeto de contacto con claves
 * arbitrarias (lo que el visitante rellenó en el bloque de contacto).
 */
export function normalizeContact(
  source: Record<string, unknown> | null | undefined,
): NormalizedContact {
  const result: NormalizedContact = { ...EMPTY_CONTACT }
  if (!source || typeof source !== 'object') return result

  const entries = Object.entries(source)

  // 1) Coincidencia por clave.
  for (const [key, raw] of entries) {
    const value = asText(raw)
    if (!value) continue
    const field = classify(key)
    if (field) assign(result, field, value)
  }

  // 2) Nombre compuesto por dos campos (nombre + apellidos).
  if (!result.fullName) {
    const first = entries.find(([k]) => plain(k).includes('firstname'))
    const last = entries.find(([k]) => plain(k).includes('lastname'))
    const composed = `${asText(first?.[1])} ${asText(last?.[1])}`.trim()
    if (composed) result.fullName = composed
  }

  // 3) Coincidencia por forma del valor, para claves irreconocibles.
  for (const [, raw] of entries) {
    const value = asText(raw)
    if (!value) continue
    if (!result.email && EMAIL_RE.test(value)) result.email = value
    else if (!result.phone && looksLikePhone(value)) result.phone = value
  }

  return result
}

/**
 * Deriva los mismos cuatro datos de las respuestas de un formulario, usando la
 * etiqueta visible de cada pregunta. Sirve para formularios donde el nombre o
 * el teléfono se preguntan como una pregunta más, no en el bloque de contacto.
 */
export function contactFromAnswers(answers: FormAnswer[]): NormalizedContact {
  const result: NormalizedContact = { ...EMPTY_CONTACT }
  for (const answer of answers ?? []) {
    const value = asText(answer.value)
    if (!value) continue
    const field = classify(answer.label ?? '')
    if (field) assign(result, field, value)
  }
  for (const answer of answers ?? []) {
    const value = asText(answer.value)
    if (!value) continue
    if (!result.email && EMAIL_RE.test(value)) result.email = value
  }
  return result
}

/** Combina varias fuentes: la primera con valor gana. */
export function mergeContact(
  ...parts: (NormalizedContact | null | undefined)[]
): NormalizedContact {
  const result: NormalizedContact = { ...EMPTY_CONTACT }
  for (const part of parts) {
    if (!part) continue
    assign(result, 'fullName', part.fullName)
    assign(result, 'phone', part.phone)
    assign(result, 'company', part.company)
    assign(result, 'email', part.email)
  }
  return result
}

/**
 * Contacto guardado junto al envío del formulario.
 *
 * `submit_public_form` recibe los datos de contacto dentro de `p_utm`, así que
 * quedan almacenados en el envío. No se asume el nombre exacto de la columna:
 * se buscan las candidatas habituales y, dentro de ellas, un objeto `contact`.
 */
export function submissionContact(
  row: Record<string, unknown>,
): NormalizedContact {
  const sources: Record<string, unknown>[] = []
  // 1. Un objeto `contact` explícito, venga donde venga.
  if (row.contact && typeof row.contact === 'object' && !Array.isArray(row.contact)) {
    sources.push(row.contact as Record<string, unknown>)
  }
  // 2. Cualquier columna jsonb del envío (utm, metadata, payload, data…): no se
  //    asume el nombre, se recorren todas y se mira también su `contact`.
  for (const value of Object.values(row)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue
    const container = value as Record<string, unknown>
    const contact = container.contact
    if (contact && typeof contact === 'object' && !Array.isArray(contact)) {
      sources.push(contact as Record<string, unknown>)
    }
    sources.push(container)
  }
  // 3. Columnas planas de la propia fila (full_name, phone, email…).
  sources.push(row)
  return mergeContact(...sources.map((source) => normalizeContact(source)))
}

export interface LeadContact {
  name: string
  phone: string
  email: string
  company: string
}

/**
 * Contacto definitivo de un lead, con una única lógica para toda la aplicación.
 *
 * Prioridad: columnas del lead (que el mapeador ya completó con el contacto del
 * envío) y, si alguna sigue vacía, las respuestas del formulario identificadas
 * por su etiqueta. Leads, embudo, ficha y exportación usan esta misma función,
 * de modo que jamás muestran datos distintos del mismo contacto.
 */
export function resolveLeadContact(lead: {
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  company?: string
  answers?: FormAnswer[]
}): LeadContact {
  const merged = mergeContact(
    {
      fullName: `${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim(),
      phone: asText(lead.phone),
      company: asText(lead.company),
      email: asText(lead.email),
    },
    contactFromAnswers(lead.answers ?? []),
  )
  return {
    name: merged.fullName,
    phone: merged.phone,
    email: merged.email,
    company: merged.company,
  }
}

/** Texto para la interfaz: nunca vacío, nunca «undefined» ni «null». */
export function dash(value?: string | null): string {
  return asText(value) || '—'
}
